import React from 'react';
import { useNavigate } from 'react-router-dom';
import GameCard from '../components/ui/GameCard';
import { websiteAnalytics, createTrackedPlayStoreUrl, TRACKING_CONFIGS } from '../utils/analytics';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const games = [
    {
      title: 'Mind Reader',
      description: 'Think of a number and I will read your mind! A magical experience that will amaze you with mathematical wizardry.',
      icon: '🔮',
      difficulty: 'Easy' as const,
      features: ['Mind Reading', 'Mathematical Magic', 'Amazing Experience'],
      route: '/game/mindreader'
    },
    {
      title: 'Number Wizard',
      description: 'Follow my magical calculations and I\'ll reveal your secret number! Experience the power of mathematical wizardry.',
      icon: '🧙‍♂️',
      difficulty: 'Easy' as const,
      features: ['Mathematical Magic', 'Number Prediction', 'Wizard Experience'],
      route: '/game/numberwizard'
    },
    {
      title: '8-Puzzle Challenge',
      description: 'Slide the numbered tiles to arrange them in order from 1 to 8. A classic sliding puzzle that challenges your spatial reasoning.',
      icon: '🧩',
      difficulty: 'Medium' as const,
      features: ['Spatial Logic', 'Problem Solving', 'Pattern Recognition'],
      route: '/game/puzzle8'
    },
    {
      title: 'Tic-Tac-Toe',
      description: 'The classic game of Xs and Os! Get three in a row to win. Perfect for quick brain training sessions.',
      icon: '⭕',
      difficulty: 'Easy' as const,
      features: ['Strategy', 'Quick Play', 'Score Tracking'],
      route: '/game/tictactoe'
    },
    {
      title: 'Sudoku',
      description: 'Fill in the 9x9 grid so that every row, column, and 3x3 box contains the digits 1-9. Choose your difficulty level!',
      icon: '🔢',
      difficulty: 'Hard' as const,
      features: ['Logic', 'Number Patterns', 'Multiple Difficulty'],
      route: '/game/sudoku'
    },
    {
      title: 'Word Puzzle',
      description: 'Unscramble the letters to form the correct word! Test your vocabulary and word recognition skills.',
      icon: '📝',
      difficulty: 'Medium' as const,
      features: ['Vocabulary', 'Word Recognition', 'Time Challenge'],
      route: '/game/wordpuzzle'
    }
  ];

  const handleGameClick = (route: string) => {
    navigate(route);
  };

  const handleWebsiteDownload = () => {
    // Track website download click with analytics
    websiteAnalytics.trackDownloadClick(
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.source,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.medium,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.campaign
    );
    
    // Create UTM-tracked Play Store URL
    const playStoreUrl = createTrackedPlayStoreUrl(
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.source,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.medium,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.campaign,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.content,
      TRACKING_CONFIGS.WEBSITE_HOMEPAGE.term
    );
    
    // Open Play Store with tracking
    window.open(playStoreUrl, '_blank');
  };

  return (
    <div className="home-page">
      <div className="hero-section">
        <h1>🎮 Welcome to JustFun!</h1>
        <p className="hero-description">
          Challenge your brain with our collection of puzzle games. 
          Play for free on the web, or download our Android app for the full experience with rewards and achievements!
        </p>
      </div>

      <div className="games-section">
        <h2>🎯 Choose Your Game</h2>
        <div className="games-grid">
          {games.map((game, index) => (
            <GameCard
              key={index}
              title={game.title}
              description={game.description}
              icon={game.icon}
              difficulty={game.difficulty}
              features={game.features}
              onClick={() => handleGameClick(game.route)}
            />
          ))}
        </div>
      </div>

      <div className="features-section">
        <h2>✨ Why Choose JustFun?</h2>
        <div className="features-grid">
          <div className="feature-item">
            <div className="feature-icon">🧩</div>
            <h3>Brain Training</h3>
            <p>Improve your cognitive skills with scientifically designed puzzle games</p>
          </div>
          <div className="feature-item">
            <div className="feature-icon">🏆</div>
            <h3>Achievements</h3>
            <p>Unlock rewards and track your progress in the Android app</p>
          </div>
          <div className="feature-item">
            <div className="feature-icon">📱</div>
            <h3>Cross-Platform</h3>
            <p>Play on web or download the Android app for the full experience</p>
          </div>
          <div className="feature-item">
            <div className="feature-icon">🆓</div>
            <h3>Free to Play</h3>
            <p>All games are completely free with no hidden costs</p>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <h2>📱 Get the Full Experience</h2>
        <p>Download our Android app to unlock:</p>
        <div className="cta-features">
          <span>🏆 Rewards System</span>
          <span>📊 Progress Tracking</span>
          <span>🎮 More Games</span>
          <span>📱 Offline Play</span>
        </div>
        <button 
          className="cta-button"
          onClick={handleWebsiteDownload}
        >
          📥 Download Android App
        </button>
      </div>
    </div>
  );
};

export default HomePage;
